import { Col, DatePicker, Form, Input, Row, Select, Switch } from 'antd'
import type { Dayjs } from 'dayjs'

export type MemberType = 'employee' | 'agent'

export type MemberFormFieldsValue = {
  type: MemberType
  name: string
  phone?: string
  email?: string
  jobTitle?: string
  isSalesperson: boolean
  hireDate?: Dayjs | null
  note?: string
}

type MemberTypeOption = {
  label: string
  value: MemberType
}

export function MemberFormFields({
  disabled = false,
  emailDisabled = false,
  emailRequired = true,
  notePlaceholder = '补充合作或任职信息',
  salespersonRequired = false,
  typeDisabled = false,
  typeOptions
}: {
  disabled?: boolean
  emailDisabled?: boolean
  emailRequired?: boolean
  notePlaceholder?: string
  salespersonRequired?: boolean
  typeDisabled?: boolean
  typeOptions: MemberTypeOption[]
}) {
  const watchedType = Form.useWatch('type') as MemberType | undefined
  const memberType = watchedType ?? typeOptions[0]?.value

  return (
    <>
      <Form.Item label="成员类型" name="type" rules={[{ message: '请选择成员类型', required: true }]}>
        <Select disabled={disabled || typeDisabled} options={typeOptions} />
      </Form.Item>
      <Row gutter={16}>
        <Col sm={12} xs={24}>
          <Form.Item
            label={memberType === 'agent' ? '代理商名称' : '姓名'}
            name="name"
            rules={[
              { max: 80, message: '名称长度不能超过 80 个字符' },
              { message: '名称长度不能少于 2 个字符', min: 2, required: true }
            ]}
          >
            <Input
              disabled={disabled}
              maxLength={80}
              placeholder={memberType === 'agent' ? '请输入代理商名称' : '请输入姓名'}
            />
          </Form.Item>
        </Col>
        {memberType === 'employee' ? (
          <Col sm={12} xs={24}>
            <Form.Item
              label="岗位"
              name="jobTitle"
              preserve={false}
              rules={[
                { max: 80, message: '岗位长度不能超过 80 个字符' },
                { message: '请输入岗位', required: true }
              ]}
            >
              <Input disabled={disabled} maxLength={80} placeholder="例如：业务员、财务" />
            </Form.Item>
          </Col>
        ) : null}
        <Col sm={12} xs={24}>
          <Form.Item
            label="邮箱"
            name="email"
            rules={[
              { message: '请输入邮箱', required: emailRequired, transform: (value) => value?.trim() },
              { message: '请输入有效的邮箱地址', type: 'email', transform: (value) => value?.trim() },
              { max: 255, message: '邮箱长度不能超过 255 个字符' }
            ]}
          >
            <Input disabled={disabled || emailDisabled} maxLength={255} placeholder="请输入联系邮箱" />
          </Form.Item>
        </Col>
        <Col sm={12} xs={24}>
          <Form.Item label="手机" name="phone" rules={[{ max: 30, message: '手机长度不能超过 30 个字符' }]}>
            <Input disabled={disabled} maxLength={30} placeholder="请输入联系电话" />
          </Form.Item>
        </Col>
        {memberType === 'employee' ? (
          <Col sm={12} xs={24}>
            <Form.Item label="入职时间" name="hireDate" preserve={false}>
              <DatePicker
                allowClear
                className="w-full"
                disabled={disabled}
                format="YYYY-MM-DD"
                placeholder="请选择入职日期"
              />
            </Form.Item>
          </Col>
        ) : null}
        <Col sm={12} xs={24}>
          <Form.Item
            label="业务功能"
            name="isSalesperson"
            required={salespersonRequired}
            rules={
              salespersonRequired
                ? [
                    {
                      validator: (_rule, value) =>
                        value ? Promise.resolve() : Promise.reject(new Error('请启用业务功能'))
                    }
                  ]
                : undefined
            }
            valuePropName="checked"
          >
            <Switch checkedChildren="已启用" disabled={disabled} unCheckedChildren="未启用" />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item label="备注" name="note">
        <Input.TextArea disabled={disabled} maxLength={2000} placeholder={notePlaceholder} rows={3} showCount />
      </Form.Item>
    </>
  )
}
